import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
  IconButton,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  JobDetail,
  JOB_NAME_LABELS,
  QUEUE_LABELS,
  JOB_STATE_COLORS,
  QueueName,
} from "types/queue";
import { useSnackbar } from "hooks/useSnackbar";

const useStyles = makeStyles((theme: any) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#1d1030",
    color: "#fff",
  },
  dialogContent: {
    padding: "24px !important",
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: 14,
    marginBottom: 8,
    color: "#1d1030",
  },
  infoRow: {
    display: "flex",
    marginBottom: 8,
    alignItems: "flex-start",
  },
  label: {
    fontWeight: 600,
    minWidth: 140,
    color: "#666",
    fontSize: 13,
  },
  value: {
    flex: 1,
    fontSize: 13,
    wordBreak: "break-word",
  },
  dataContainer: {
    backgroundColor: "#f5f5f5",
    padding: 12,
    borderRadius: 4,
    position: "relative",
    maxHeight: 300,
    overflow: "auto",
  },
  codeBlock: {
    fontFamily: "monospace",
    fontSize: 12,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    margin: 0,
  },
  stacktraceContainer: {
    backgroundColor: "#fff3f3",
    padding: 12,
    borderRadius: 4,
    border: "1px solid #ffcdd2",
    maxHeight: 250,
    overflow: "auto",
  },
  stacktraceLine: {
    fontFamily: "monospace",
    fontSize: 11,
    color: "#c62828",
    marginBottom: 4,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  copyButton: {
    position: "absolute",
    top: 4,
    right: 4,
  },
  chip: {
    fontWeight: 600,
    fontSize: 12,
  },
  attemptsWarning: {
    backgroundColor: "#fff3e0",
    color: "#e65100",
  },
}));

interface JobDetailModalProps {
  open: boolean;
  onClose: () => void;
  job: JobDetail | null;
  queueName: QueueName;
}

const JobDetailModal: React.FC<JobDetailModalProps> = ({
  open,
  onClose,
  job,
  queueName,
}) => {
  const classes = useStyles();
  const { showSnackbar } = useSnackbar();

  if (!job) return null;

  const formatTimestamp = (ts: number | undefined) => {
    if (!ts) return "-";
    return new Date(ts).toLocaleString();
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    showSnackbar(`${label} copied to clipboard`, "success");
  };

  const getStateColor = (state: string) => {
    return (
      JOB_STATE_COLORS[state as keyof typeof JOB_STATE_COLORS] || "#9e9e9e"
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Box>
          <Typography variant="h6" component="span">
            Job Details
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            {QUEUE_LABELS[queueName]} - {JOB_NAME_LABELS[job.name] || job.name}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {/* Basic Info Section */}
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            Basic Information
          </Typography>
          <Box className={classes.infoRow}>
            <Typography className={classes.label}>Job ID:</Typography>
            <Typography className={classes.value}>{job.id}</Typography>
          </Box>
          <Box className={classes.infoRow}>
            <Typography className={classes.label}>Job Name:</Typography>
            <Typography className={classes.value}>
              {JOB_NAME_LABELS[job.name] || job.name}
            </Typography>
          </Box>
          <Box className={classes.infoRow}>
            <Typography className={classes.label}>State:</Typography>
            <Chip
              label={job.state.toUpperCase()}
              size="small"
              className={classes.chip}
              sx={{ backgroundColor: getStateColor(job.state), color: "#fff" }}
            />
          </Box>
          <Box className={classes.infoRow}>
            <Typography className={classes.label}>Attempts Made:</Typography>
            <Chip
              label={`${job.attemptsMade} / ${job.opts?.attempts || "∞"}`}
              size="small"
              className={`${classes.chip} ${
                job.attemptsMade >= (job.opts?.attempts || 5)
                  ? classes.attemptsWarning
                  : ""
              }`}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Timestamps Section */}
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>Timestamps</Typography>
          <Box className={classes.infoRow}>
            <Typography className={classes.label}>Created:</Typography>
            <Typography className={classes.value}>
              {formatTimestamp(job.timestamp)}
            </Typography>
          </Box>
          <Box className={classes.infoRow}>
            <Typography className={classes.label}>Processed On:</Typography>
            <Typography className={classes.value}>
              {formatTimestamp(job.processedOn)}
            </Typography>
          </Box>
          <Box className={classes.infoRow}>
            <Typography className={classes.label}>Finished On:</Typography>
            <Typography className={classes.value}>
              {formatTimestamp(job.finishedOn)}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Job Data Section */}
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>Job Data</Typography>
          <Box className={classes.dataContainer}>
            <IconButton
              className={classes.copyButton}
              size="small"
              onClick={() =>
                copyToClipboard(JSON.stringify(job.data, null, 2), "Job data")
              }
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
            <pre className={classes.codeBlock}>
              {JSON.stringify(job.data, null, 2)}
            </pre>
          </Box>
        </Box>

        {/* Job Options Section */}
        {job.opts && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box className={classes.section}>
              <Typography className={classes.sectionTitle}>
                Job Options
              </Typography>
              <Box className={classes.dataContainer}>
                <pre className={classes.codeBlock}>
                  {JSON.stringify(job.opts, null, 2)}
                </pre>
              </Box>
            </Box>
          </>
        )}

        {/* Error Section (if failed or has stacktrace) */}
        {(job.failedReason ||
          (job.stacktrace && job.stacktrace.length > 0)) && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box className={classes.section}>
              <Typography
                className={classes.sectionTitle}
                sx={{ color: "#c62828" }}
              >
                Error Information
              </Typography>
              {job.failedReason && (
                <Box className={classes.infoRow}>
                  <Typography className={classes.label}>
                    Failed Reason:
                  </Typography>
                  <Typography
                    className={classes.value}
                    sx={{ color: "#c62828" }}
                  >
                    {job.failedReason}
                  </Typography>
                </Box>
              )}
              {job.stacktrace && job.stacktrace.length > 0 && (
                <Box mt={2}>
                  <Typography
                    className={classes.label}
                    sx={{ marginBottom: 1, display: "block" }}
                  >
                    Stacktrace:
                  </Typography>
                  <Box className={classes.stacktraceContainer}>
                    <IconButton
                      className={classes.copyButton}
                      size="small"
                      onClick={() =>
                        copyToClipboard(
                          job.stacktrace!.join("\n"),
                          "Stacktrace"
                        )
                      }
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                    {job.stacktrace.map((line, index) => (
                      <Typography
                        key={index}
                        className={classes.stacktraceLine}
                      >
                        {line}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default JobDetailModal;
